import { type FormEvent, type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { type Result } from '@vello/core';
import { type ApiError, type FoodOpsInterventionView, type FoodOpsTargetType } from '@vello/api-client';
import { type FormatContext } from '@vello/format';
import { tailOf, interventionActions, interventionTone, partitionInterventions, type ConsoleTone, formatMarketInstant, marketZoneOfContext, scopeTimeZone } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, EmptyState, Field, Notice, PageHeader, Panel, SegmentedControl, Select, TextArea, TextInput, FormRow, SectionTitle, Stack, Text, Row, Strong, Split, PanelHead, PanelBody, Box, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import { LoadingBlock } from '../components/loading-block';
import { IdRef } from '../components/code-ref';
import { usePersonName } from '../components/person-name';

const TARGET_TYPES: readonly FoodOpsTargetType[] = ['order', 'queue', 'merchant', 'courier'];
const TONE_BADGE: Readonly<Record<ConsoleTone, 'neutral' | 'success' | 'danger' | 'warning'>> = { neutral: 'neutral', success: 'success', danger: 'danger', warning: 'warning' };

/**
 * **Operações Food** (Food Ops, M19/S193) — the platform operator surface over `/v1/food-ops/*`. An operator opens
 * an intervention on a live target (a slipping order, a queue SLA breach, a stuck merchant/courier) and drives its
 * lifecycle — open → acknowledge → escalate → resolve. It lists the open set, or every intervention on one target.
 * Server-authoritative (operator AAL2 + a scoped `config:*` grant); status→tone, action gating and the
 * open/resolved split are the pure `@vello/admin` helpers.
 */
export function FoodOpsScreen(): ReactElement {
  const { runtime, scope: operatorScope, markets } = useAdmin();
  const { toast } = useConsole();
  const { t, lang } = useT();
  const { fail } = useVocab();
  // D401: instants render in the operating market's zone (AGENTS: store UTC, render in the market zone).
  // When the scope names no single zone the render falls back to UTC and SAYS so (`formatMarketInstant`).
  const fmtCtx: FormatContext = { locale: lang, timeZone: scopeTimeZone(operatorScope.current, markets) ?? 'UTC', measurement: 'metric' };

  const [scope, setScope] = useState<'open' | 'target'>('open');
  const [targetRef, setTargetRef] = useState('');
  const [list, setList] = useState<readonly FoodOpsInterventionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorId, setErrorId] = useState<string | undefined>(undefined);

  /** `quiet` is the background re-read: the cards stay on screen (no loading block) while it runs. */
  const load = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) setLoading(true);
    setErrorId(undefined);
    const res = scope === 'target' ? await runtime.foodOps.listByTarget(targetRef.trim()) : await runtime.foodOps.listOpen();
    if (res.ok) setList(res.value.interventions);
    else setErrorId(t('food.err', { code: fail(res.error) }));
    if (!quiet) setLoading(false);
  }, [runtime, scope, targetRef, t]);

  useEffect(() => {
    if (scope === 'open') void load();
  }, [scope, load]);
  useAutoRefresh(() => load(true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: scope === 'open' });

  const { open, resolved } = useMemo(() => partitionInterventions(list), [list]);

  return (
    <>
      <PageHeader title={t('foodOps.title')} subtitle={t('foodOps.subtitle')} />

      <Split aside="lg" gap={6}>
        <OpenInterventionForm onOpened={() => { toast({ message: t('foodOps.open.ok'), tone: 'success' }); void load(); }} onError={(code) => toast({ message: t('food.err', { code }), tone: 'warning' })} />

        <Stack gap={4}>
          <Panel>
            
              <SegmentedControl
                ariaLabel={t('foodOps.filter.title')}
                value={scope}
                onChange={(id) => setScope(id)}
                options={[{ id: 'open', label: t('foodOps.filter.open') }, { id: 'target', label: t('foodOps.filter.byTarget') }]}
              />
            
            {scope === 'target' && (
              <FormRow
                action={
                  <Button variant="secondary" onClick={() => void load()} disabled={targetRef.trim().length === 0 || loading}>{t('foodOps.action.lookup')}</Button>
                }
              >
                <Field label={t('foodOps.field.targetRef')} htmlFor="fo-target">
                  <TextInput id="fo-target" value={targetRef} onChange={(e) => setTargetRef(e.target.value)} placeholder={t('foodOps.field.targetRef.ph')} />
                </Field>
              </FormRow>
            )}
          </Panel>

          {errorId !== undefined && <Notice tone="danger">{errorId}</Notice>}

          <Panel padded={false}>
            <PanelHead title={t('foodOps.section.open')} actions={<Badge tone="neutral">{String(open.length)}</Badge>} />
            <PanelBody><Stack gap={3}>
              {loading ? (
                <LoadingBlock />
              ) : open.length === 0 ? (
                <EmptyState title={t('foodOps.empty.open')} detail={t('foodOps.empty.open.detail')} />
              ) : (
                open.map((iv) => <InterventionCard key={iv.interventionId} iv={iv} fmtCtx={fmtCtx} onChanged={() => void load()} />)
              )}
            </Stack></PanelBody>
          </Panel>

          {scope === 'target' && resolved.length > 0 && (
            <Panel padded={false}>
              <PanelHead title={t('foodOps.section.resolved')} actions={<Badge tone="success">{String(resolved.length)}</Badge>} />
              <PanelBody><Stack gap={3}>
                {resolved.map((iv) => <InterventionCard key={iv.interventionId} iv={iv} fmtCtx={fmtCtx} onChanged={() => void load()} />)}
              </Stack></PanelBody>
            </Panel>
          )}
        </Stack>
      </Split>
    </>
  );
}

function OpenInterventionForm({ onOpened, onError }: { onOpened: () => void; onError: (code: string) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const [targetType, setTargetType] = useState<FoodOpsTargetType>('order');
  const [targetRef, setTargetRef] = useState('');
  const [kind, setKind] = useState<'flag' | 'escalate'>('flag');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = targetRef.trim().length > 0 && reasonCode.trim().length > 0;

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy || !valid) return;
    setBusy(true);
    const res = await runtime.foodOps.openIntervention({
      targetType,
      targetRef: targetRef.trim(),
      kind,
      reasonCode: reasonCode.trim(),
      ...(note.trim().length > 0 ? { note: note.trim() } : {}),
    });
    setBusy(false);
    if (res.ok) {
      setTargetRef('');
      setReasonCode('');
      setNote('');
      onOpened();
    } else {
      onError(res.error.code);
    }
  };

  return (
    <Panel>
      <SectionTitle>{t('foodOps.open.title')}</SectionTitle>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <Field label={t('foodOps.field.targetType')} htmlFor="fo-type">
          <Select id="fo-type" value={targetType} onChange={(e) => setTargetType(e.target.value as FoodOpsTargetType)}>
            {TARGET_TYPES.map((tt) => <option key={tt} value={tt}>{v('food.ops_target', tt)}</option>)}
          </Select>
        </Field>
        <Field label={t('foodOps.field.targetRef')} htmlFor="fo-open-ref">
          <TextInput id="fo-open-ref" value={targetRef} onChange={(e) => setTargetRef(e.target.value)} placeholder={t('foodOps.field.targetRef.ph')} />
        </Field>
        <Field label={t('foodOps.field.kind')} htmlFor="fo-kind" hint={t('foodOps.field.kind.hint')}>
          <Select id="fo-kind" value={kind} onChange={(e) => setKind(e.target.value as 'flag' | 'escalate')}>
            <option value="flag">{v('food.ops_intervention_kind', 'flag')}</option>
            <option value="escalate">{v('food.ops_intervention_kind', 'escalate')}</option>
          </Select>
        </Field>
        <Field label={t('foodOps.field.reasonCode')} htmlFor="fo-reason" hint={t('foodOps.field.reasonCode.hint')}>
          <TextInput id="fo-reason" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} placeholder={t('foodOps.field.reasonCode.ph')} />
        </Field>
        <Field label={t('foodOps.field.note')} htmlFor="fo-note">
          <TextArea id="fo-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('foodOps.field.note.ph')} />
        </Field>
        <Button type="submit" variant="primary" block disabled={busy || !valid}>{busy ? t('foodOps.open.busy') : t('foodOps.open.submit')}</Button>
      </form>
    </Panel>
  );
}

function InterventionCard({ iv, fmtCtx, onChanged }: { iv: FoodOpsInterventionView; fmtCtx: FormatContext; onChanged: () => void }): ReactElement {
  const { runtime } = useAdmin();
  const { toast } = useConsole();
  const { t } = useT();
  const { fail, v } = useVocab();
  const actions = interventionActions(iv.status);
  const openedByName = usePersonName(iv.openedBy);
  const [pending, setPending] = useState<'escalate' | 'resolve' | undefined>(undefined);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<Result<unknown, ApiError>>, okId: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    const res = await fn();
    setBusy(false);
    if (res.ok) {
      setPending(undefined);
      setText('');
      toast({ message: t(okId), tone: 'success' });
      onChanged();
    } else {
      toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' });
    }
  };

  const confirmPending = (): void => {
    const value = text.trim();
    if (value.length === 0) return;
    if (pending === 'escalate') void run(() => runtime.foodOps.escalate(iv.interventionId, value), 'foodOps.toast.escalated');
    else if (pending === 'resolve') void run(() => runtime.foodOps.resolve(iv.interventionId, value), 'foodOps.toast.resolved');
  };

  return (
    <Box gap={2} tone="subtle">
      <Row gap={2} align="baseline" justify="between">
        <Strong role="body">{v('food.ops_target', iv.targetType)} · <IdRef value={iv.targetRef} /></Strong>
        <Badge tone={TONE_BADGE[interventionTone(iv.status)]}>{v('food.ops_intervention_status', iv.status)}</Badge>
      </Row>
      <Text as="div" role="caption" tone="secondary"><Row gap={2} align="stretch" wrap>
        <span>{v('food.ops_intervention_kind', iv.kind)}</span>
        <span aria-hidden="true">·</span>
        <span className="tnum">{iv.reasonCode}</span>
      </Row></Text>
      <Text as="div" role="caption" tone="tertiary">{t('foodOps.card.opened', { at: formatMarketInstant(iv.openedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale), by: openedByName ?? tailOf(iv.openedBy) })}</Text>
      {iv.note !== undefined && iv.note.length > 0 && <Text as="p" role="caption" tone="secondary">{iv.note}</Text>}
      {iv.status === 'resolved' && iv.resolutionNote !== undefined && <Text as="p" role="caption" tone="success">{t('foodOps.card.resolution', { note: iv.resolutionNote })}</Text>}

      {pending !== undefined ? (
        <Stack gap={2}>
          <TextInput aria-label={t(pending === 'escalate' ? 'foodOps.field.escalateReason' : 'foodOps.field.resolutionNote')} value={text} onChange={(e) => setText(e.target.value)} placeholder={t(pending === 'escalate' ? 'foodOps.field.escalateReason.ph' : 'foodOps.field.resolutionNote.ph')} />
          <Row gap={2} align="stretch">
            <Button size="sm" variant={pending === 'escalate' ? 'danger' : 'secondary'} onClick={confirmPending} disabled={busy || text.trim().length === 0}>{t(pending === 'escalate' ? 'foodOps.action.escalate' : 'foodOps.action.resolve')}</Button>
            <Button size="sm" variant="ghost" onClick={() => { setPending(undefined); setText(''); }} disabled={busy}>{t('ui.cancel')}</Button>
          </Row>
        </Stack>
      ) : (
        (actions.canAcknowledge || actions.canEscalate || actions.canResolve) && (
          <Row gap={2} align="stretch" wrap>
            {actions.canAcknowledge && <Button size="sm" variant="ghost" onClick={() => void run(() => runtime.foodOps.acknowledge(iv.interventionId), 'foodOps.toast.acknowledged')} disabled={busy}>{t('foodOps.action.acknowledge')}</Button>}
            {actions.canEscalate && <Button size="sm" variant="danger" onClick={() => setPending('escalate')} disabled={busy}>{t('foodOps.action.escalate')}</Button>}
            {actions.canResolve && <Button size="sm" variant="secondary" onClick={() => setPending('resolve')} disabled={busy}>{t('foodOps.action.resolve')}</Button>}
          </Row>
        )
      )}
    </Box>
  );
}
